import { defineStore } from 'pinia'
import * as bingoApi from '../services/bingoApi.js'
import { ROUND_STATUS } from '../constants.js'

export { ROUND_STATUS }

export const useGameStore = defineStore('game', {
  state: () => ({
    game: null,
    round: null,
    card: null,
    loading: false,
    error: null,
    failedClaim: null,
  }),

  getters: {
    roundStatus: (state) => state.round?.status ?? null,
    isPlaying: (state) => state.round?.status === ROUND_STATUS.PLAYING,
    isPreparing: (state) => state.round?.status === ROUND_STATUS.PREPARING,
    isEnded: (state) => state.round?.status === ROUND_STATUS.ENDED,
    hasActiveRound: (state) => !!state.round && state.round.status !== ROUND_STATUS.ENDED,
    hasCard: (state) => !!state.card,
    isBingoClaimed: (state) => {
      if (!state.card) return false
      if ('verified_winner' in state.card) return !!state.card.verified_winner

      // Fallback for if the api did not have verified_winner in it,
      // than we don't have the 3 claims stuff.
      return !!state.card.bingo_claimed_at
    },
    isClaimBlocked: (state) => state.card?.attempts_left === 0,
    hasNextRound: (state) => {
      if (!state.game?.current_round) return false
      return state.game.current_round.id !== state.round?.id
    },

    gridSquares: (state) => {
      if (!state.card?.card_squares) return []
      const rows = state.game?.grid_rows ?? 3
      const cols = state.game?.grid_cols ?? 3
      const grid = Array.from({ length: rows }, () => Array(cols).fill(null))
      for (const cs of state.card.card_squares) {
        grid[cs.grid_row][cs.grid_col] = cs
      }
      return grid
    },

    allMarked: (state) => {
      if (!state.card?.card_squares?.length) return false
      return state.card.card_squares.every((cs) => cs.marked)
    },

    canClaimBingo() {
      return this.isPlaying && this.allMarked && !this.isBingoClaimed && !this.isClaimBlocked
    },
  },

  actions: {
    async loadGame(gameId) {
      this.loading = true
      this.error = null
      try {
        this.game = await bingoApi.fetchGame(gameId)
        this.round = this.game.current_round ?? null
        console.log('[bingo] loaded game, round:', JSON.stringify(this.round))
      } catch (e) {
        this.error = 'Kon het spel niet laden.'
        throw e
      } finally {
        this.loading = false
      }
    },

    async createOrLoadCard(gameId) {
      this.loading = true
      this.error = null
      try {
        this.card = await bingoApi.createCard(gameId)
      } catch (e) {
        this.error = 'Kon geen bingokaart aanmaken.'
        throw e
      } finally {
        this.loading = false
      }
    },

    async toggleSquare(cardSquare) {
      if (!this.card) return
      const wasMarked = cardSquare.marked
      cardSquare.marked = !wasMarked

      try {
        if (wasMarked) {
          await bingoApi.unmarkSquare(this.card.id, cardSquare.id)
        } else {
          await bingoApi.markSquare(this.card.id, cardSquare.id)
        }
      } catch {
        cardSquare.marked = wasMarked
      }
    },

    async claimBingo() {
      if (!this.card) return
      try {
        const result = await bingoApi.claimBingo(this.card.id)
        this.card.bingo_claimed_at = result.bingo_claimed_at

        // Fallback for if the api did not valid in it, then we don't
        // have the 3 claims stuff, so just a win
        if (!('valid' in result)) {
          this.card.verified_winner = !!result.bingo_claimed_at
          return
        }

        this.card.verified_winner = result.valid
        this.card.attempts_left = result.attempts_left
        this.failedClaim = result.valid
          ? null
          : { attemptsLeft: this.card.attempts_left, wrongfullyMarkedCount: result.wrongfully_marked_count ?? 0 }
      } catch {
        this.error = 'Kon bingo niet claimen.'
      }
    },

    setSquareMarked(cardSquareId, marked) {
      const square = this.card?.card_squares?.find((cs) => cs.id === cardSquareId)
      if (square) square.marked = marked
    },

    updateRoundStatus(status, roundId) {
      if (this.round && this.round.id === roundId) {
        this.round.status = status
      } else if (!this.round && roundId) {
        this.round = { id: roundId, status }
      }
    },

    async reloadCard() {
      if (!this.card?.id) return
      try {
        this.card = await bingoApi.fetchCard(this.card.id)
      } catch {
        // silently fail
      }
    },

    async reloadGame(gameId) {
      try {
        this.game = await bingoApi.fetchGame(gameId)
      } catch {
        // silently fail
      }
    },
  },
})
